import React from 'react'
import DocumentDialog, { DocumentSection } from '../DocumentDialog'

interface CommercialInvoiceDialogNewProps {
  invoiceData: any
  schema: any
  onSave?: (updatedData: any, changes?: any[]) => void
  shipmentId: string
  documentId: string
}

const CommercialInvoiceDialog: React.FC<CommercialInvoiceDialogNewProps> = ({
  invoiceData,
  schema,
  onSave,
  shipmentId,
  documentId,
}) => {
  // Define sections for Commercial Invoice
  const sections: DocumentSection[] = [
    {
      id: 'invoiceInfo',
      title: 'Invoice Information',
      layout: 'double',
      fields: [
        { type: 'field', label: 'Invoice #', path: 'invoiceNumber' },
        { type: 'field', label: 'Invoice Date', path: 'invoiceDate' },
        { type: 'field', label: 'Due Date', path: 'dueDate' },
        { type: 'field', label: 'PO #', path: 'poNumber' },
      ],
    },
    {
      id: 'totals',
      title: 'Totals',
      layout: 'double',
      fields: [
        { type: 'field', label: 'Subtotal', path: 'subtotal', complexType: 'currency' },
        { type: 'field', label: 'Tax Amount', path: 'taxAmount', complexType: 'currency' },
        { type: 'field', label: 'Tax Rate (%)', path: 'taxRate' },
        { type: 'field', label: 'Shipping Cost', path: 'shippingCost', complexType: 'currency' },
        { type: 'field', label: 'Discount Amount', path: 'discountAmount', complexType: 'currency' },
        { type: 'field', label: 'Total Amount', path: 'totalAmount', complexType: 'currency' },
      ],
    },
    {
      id: 'parties',
      title: 'Parties',
      layout: 'grid',
      fields: [
        {
          type: 'object',
          label: 'Seller',
          path: 'seller',
          fields: [
            { label: 'Name', path: 'name' },
            { label: 'Address', path: 'address' },
            { label: 'City', path: 'city' },
            { label: 'State', path: 'state' },
            { label: 'Country', path: 'country' },
            { label: 'Postal Code', path: 'postalCode' },
            { label: 'Tax ID', path: 'taxId' },
            { label: 'Phone', path: 'phone' },
            { label: 'Email', path: 'email' },
          ],
        },
        {
          type: 'object',
          label: 'Buyer',
          path: 'buyer',
          fields: [
            { label: 'Name', path: 'name' },
            { label: 'Address', path: 'address' },
            { label: 'City', path: 'city' },
            { label: 'State', path: 'state' },
            { label: 'Country', path: 'country' },
            { label: 'Postal Code', path: 'postalCode' },
            { label: 'Tax ID', path: 'taxId' },
            { label: 'Phone', path: 'phone' },
            { label: 'Email', path: 'email' },
          ],
        },
      ],
    },
    {
      id: 'shipTo',
      title: 'Ship To',
      layout: 'single',
      fields: [
        {
          type: 'object',
          label: 'Ship To',
          path: 'shipTo',
          fields: [
            { label: 'Name', path: 'name' },
            { label: 'Address', path: 'address' },
            { label: 'City', path: 'city' },
            { label: 'State', path: 'state' },
            { label: 'Country', path: 'country' },
            { label: 'Postal Code', path: 'postalCode' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'shipmentDetails',
      title: 'Shipment Details',
      layout: 'double',
      fields: [
        {
          type: 'object',
          label: 'Shipment Details',
          path: 'shipmentDetails',
          fields: [
            { label: 'Ship Date', path: 'shipDate' },
            { label: 'Ship Via', path: 'shipVia' },
            { label: 'Tracking #', path: 'trackingNumber' },
            { label: 'Incoterms', path: 'incoterms' },
            { label: 'Delivery Terms', path: 'deliveryTerms' },
          ],
        },
      ],
    },
    {
      id: 'items',
      title: 'Items',
      layout: 'single',
      fields: [
        {
          type: 'array',
          label: 'Items',
          path: 'items',
          columns: [
            { label: 'Description', path: 'description' },
            { label: 'Quantity', path: 'quantity' },
            {
              label: 'Unit Price',
              path: 'unitPrice',
              complexType: 'currency',
            },
            {
              label: 'Total Amount',
              path: 'totalAmount',
              complexType: 'currency',
            },
            {
              label: 'Weight',
              path: 'weight',
              complexType: 'weight',
            },
            {
              label: 'Volume',
              path: 'volume',
              complexType: 'volume',
            },
            {
              label: 'Dimensions',
              path: 'dimensions',
              complexType: 'dimensions',
            },
            { label: 'SKU', path: 'sku' },
            { label: 'Part #', path: 'partNumber' },
            { label: 'Country of Origin', path: 'countryOfOrigin' },
            { label: 'HS Code', path: 'hsCode' },
          ],
        },
      ],
    },
    {
      id: 'paymentInfo',
      title: 'Payment Information',
      layout: 'grid',
      fields: [
        { type: 'field', label: 'Payment Terms', path: 'paymentTerms' },
        { type: 'field', label: 'Payment Method', path: 'paymentMethod' },
        {
          type: 'object',
          label: 'Bank Details',
          path: 'bankDetails',
          fields: [
            { label: 'Bank Name', path: 'bankName' },
            { label: 'Account #', path: 'accountNumber' },
            { label: 'Routing #', path: 'routingNumber' },
            { label: 'SWIFT Code', path: 'swiftCode' },
            { label: 'IBAN', path: 'iban' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'customsInfo',
      title: 'Customs Information',
      layout: 'single',
      fields: [
        {
          type: 'object',
          label: 'Customs Information',
          path: 'customsInfo',
          fields: [
            { label: 'Export License #', path: 'exportLicenseNumber' },
            { label: 'Import License #', path: 'importLicenseNumber' },
            { label: 'Declaration Statement', path: 'declarationStatement' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'additionalInfo',
      title: 'Additional Information',
      layout: 'single',
      fields: [
        { type: 'field', label: 'Notes', path: 'notes', fullWidth: true },
        { type: 'field', label: 'Terms and Conditions', path: 'termsAndConditions', fullWidth: true },
      ],
    },
  ]

  return (
    <DocumentDialog
      documentData={invoiceData}
      schema={schema}
      sections={sections}
      documentType="Commercial Invoice"
      onSave={onSave}
      shipmentId={shipmentId}
      documentId={documentId}
    />
  )
}

export default CommercialInvoiceDialog
